import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../profile-components/Navbar";
import Topbar from "../profile-components/Topbar";
import Infobar from "../../components/profile-components/Infobar";
import API_BASE_URL from "../../config";
import "../../pages/LeadProfile.css";
import "../../pages/SalesPages.css";

function SOACompletePage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [record, setRecord] = useState(null);
  const [recordType, setRecordType] = useState("");
  const [recordId, setRecordId] = useState(null);

  const [selectedProducts, setSelectedProducts] = useState({});
  const [beneficiary, setBeneficiary] = useState({});
  const [representative, setRepresentative] = useState(null);
  const [agent, setAgent] = useState({
    first_name: "",
    last_name: "",
  });
  const [beneficiarySubmittedAt, setBeneficiarySubmittedAt] = useState("");

  const [appointmentDate, setAppointmentDate] = useState("");
  const [initialMethodOfContact, setInitialMethodOfContact] = useState("");
  const [agreement, setAgreement] = useState(true);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const productOptions = {
    mapd: "Medicare Advantage (Part C)",
    pdp: "Prescription Drug Plan (Part D)",
    medsupp: "Medicare Supplement (Medigap)",
    dental: "Dental / Vision / Hearing",
    hospital: "Hospital Indemnity",
  };

  const pageTitle = useMemo(() => {
    const first = record?.first_name || beneficiary.first_name || "";
    const last = record?.last_name || beneficiary.last_name || "";
    return `${first} ${last}`.trim() || "SOA Completion";
  }, [record, beneficiary]);

  const agentName = useMemo(() => {
    return `${agent.first_name || ""} ${agent.last_name || ""}`.trim();
  }, [agent]);

  const recordProfilePath = useMemo(() => {
    if (!recordId || !recordType) return "";

    if (recordType === "lead") return `/lead-profile/${recordId}`;
    if (recordType === "client") return `/client-profile/${recordId}`;

    return "";
  }, [recordId, recordType]);

  useEffect(() => {
    const tokenAuth = localStorage.getItem("token");

    if (!tokenAuth) {
      sessionStorage.setItem(
        "redirect_after_login",
        window.location.pathname + window.location.search
      );
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const tokenAuth = localStorage.getItem("token");

    if (!tokenAuth) return;

    const loadSession = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/soa/session/${token}`);

        if (!res.ok) {
          setLoading(false);
          return;
        }

        const data = await res.json();

        setRecord(data.record || null);
        setRecordType(data.record_type || "");
        setRecordId(data.record_id || null);

        const preselected = {};
        Object.keys(productOptions).forEach((key) => {
          preselected[key] = data.products?.includes(key);
        });

        setSelectedProducts(preselected);

        setBeneficiary({
          first_name: data.record?.first_name || "",
          last_name: data.record?.last_name || "",
          street_address: data.record?.street_address || "",
          city: data.record?.city || "",
          state: data.record?.state || "",
          zip_code: data.record?.zip_code || "",
          phone: data.record?.phone || "",
        });

        setAgent({
          first_name:
            data.agent?.first_name || data.agent?.name?.split(" ")[0] || "",
          last_name:
            data.agent?.last_name ||
            data.agent?.name?.split(" ").slice(1).join(" ") ||
            "",
        });

        setBeneficiarySubmittedAt(data.signed_at || "");
        setLoading(false);
      } catch (err) {
        console.error(err);
        setLoading(false);
      }
    };

    loadSession();
  }, [token]);

  const handleSubmit = async () => {
    if (!appointmentDate || !initialMethodOfContact) {
      alert("All required fields must be completed.");
      return;
    }

    if (!agreement) {
      alert("You must confirm before submitting.");
      return;
    }

    try {
      setSubmitting(true);

      const authToken = localStorage.getItem("token");

      if (!authToken) {
        sessionStorage.setItem(
          "redirect_after_login",
          window.location.pathname + window.location.search
        );
        navigate("/login", { replace: true });
        return;
      }

      const res = await fetch(`${API_BASE_URL}/api/soa/complete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          token,
          beneficiary,
          representative,
          selectedProducts,
          appointment_date: appointmentDate,
          initial_method_of_contact: initialMethodOfContact,
          agreement_confirmed: agreement,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.message || "Unable to complete SOA.");
        return;
      }

      if (data.record_type === "lead") {
        navigate(`/lead-profile/${data.record_id}?tab=Record%20History`);
      }

      if (data.record_type === "client") {
        navigate(`/client-profile/${data.record_id}?tab=Record%20History`);
      }
    } catch (err) {
      console.error(err);
      alert("Error completing SOA.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenEmailComposer = () => {
    if (!record?.email) return;
    window.location.href = `mailto:${record.email}`;
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <Topbar />

        <div className="main-content" style={{ display: "flex" }}>
          <div className="profile-content sales-page">
            <div className="sales-section-card">
              <div className="sales-section-head">
                <h2>Loading Scope of Appointment...</h2>
                <p>Please wait while we load this SOA.</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!record) {
    return (
      <>
        <Navbar />
        <Topbar />

        <div className="main-content" style={{ display: "flex" }}>
          <div className="profile-content sales-page">
            <div className="sales-section-card">
              <div className="sales-section-head">
                <h2>Invalid or Expired Link</h2>
                <p>This Scope of Appointment link is invalid or expired.</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content" style={{ display: "flex" }}>
        <div className="profile-content sales-page">
          <div className="sales-hero sales-profile-hero">
            <div>
              <p className="sales-eyebrow">Scope of Appointment</p>
              <h1 className="sales-title">{pageTitle}</h1>
              <p className="sales-subtitle">
                Review the signed SOA, add appointment details, and finalize the
                completed PDF for the record history.
              </p>

              <div className="sales-hero-meta">
                <span className="sales-chip sales-chip-outline">
                  Beneficiary Submitted:{" "}
                  {beneficiarySubmittedAt
                    ? new Date(beneficiarySubmittedAt).toLocaleString()
                    : "Not submitted"}
                </span>

                <span className="sales-chip sales-chip-outline">
                  Agent: {agentName || "Not assigned"}
                </span>
              </div>
            </div>

            <div className="sales-header-actions">
              {recordProfilePath && (
                <button
                  type="button"
                  className="sales-secondary-btn"
                  onClick={() => navigate(recordProfilePath)}
                >
                  Back to Profile
                </button>
              )}
            </div>
          </div>

          <div className="sales-section-card sales-section-lead">
            <div className="sales-section-head">
              <h2>Scope of Appointment Confirmation Form</h2>
              <p>
                Review the beneficiary-submitted form and complete the
                agent-required appointment details below.
              </p>
            </div>

            <p className="sales-secondary" style={{ lineHeight: 1.6 }}>
              The Centers for Medicare and Medicaid Services requires agents to
              document the scope of a marketing appointment prior to any
              face-to-face sales meeting to ensure understanding of what will be
              discussed between the agent and the Medicare beneficiary, or their
              authorized representative. All information provided on this form is
              confidential and should be completed by each person with Medicare or
              their authorized representative.
            </p>

            <div className="section">
              <h3 className="section-title">
                Please check the types of product(s) you want the agent to
                discuss.
              </h3>

              <div className="soa-product-list">
                {Object.keys(productOptions).map((key) => (
                  <label key={key} className="soa-product-row">
                    <input
                      type="checkbox"
                      checked={Boolean(selectedProducts[key])}
                      disabled
                      readOnly
                    />
                    <span>{productOptions[key]}</span>
                  </label>
                ))}
              </div>
            </div>

            <p className="sales-secondary" style={{ lineHeight: 1.6 }}>
              By signing this form, you agree to a meeting with a sales agent to
              discuss the types of products you indicated above. Please note, the
              person who will discuss the products is either employed or
              contracted by a Medicare plan. They do not work directly for the
              federal government. This individual may also be paid based on your
              enrollment in a plan. Signing this form does not obligate you to
              enroll in a plan, affect your current enrollment, or enroll you in
              a Medicare plan.
            </p>

            <div className="section">
              <h3 className="section-title">
                Please fill out the following details:
              </h3>

              <p className="sales-secondary" style={{ lineHeight: 1.6 }}>
                Note: Each person enrolled in Medicare receives a Medicare
                Beneficiary Identifier number on their Medicare card. Enter the
                information of the primary Medicare beneficiary.
              </p>

              <div className="sales-form-fields">
                <div className="sales-field">
                  <label>First Name</label>
                  <input value={beneficiary.first_name || ""} disabled />
                </div>

                <div className="sales-field">
                  <label>Last Name</label>
                  <input value={beneficiary.last_name || ""} disabled />
                </div>

                <div className="sales-field sales-field-full">
                  <label>Street Address</label>
                  <input value={beneficiary.street_address || ""} disabled />
                </div>

                <div className="sales-field">
                  <label>City</label>
                  <input value={beneficiary.city || ""} disabled />
                </div>

                <div className="sales-field">
                  <label>State</label>
                  <input value={beneficiary.state || ""} disabled />
                </div>

                <div className="sales-field">
                  <label>Zip Code</label>
                  <input value={beneficiary.zip_code || ""} disabled />
                </div>

                <div className="sales-field">
                  <label>Phone</label>
                  <input value={beneficiary.phone || ""} disabled />
                </div>
              </div>
            </div>

            <div className="section">
              <h3 className="section-title">Beneficiary Agreement</h3>

              <label className="soa-product-row">
                <input type="checkbox" checked disabled readOnly />
                <span>
                  By checking this box, the beneficiary confirmed they have read
                  and understand the contents of the Scope of Appointment form,
                  and confirmed that the information provided is accurate. If
                  submitted by an authorized individual, this submission certifies
                  that this person is authorized under State law to complete the
                  Scope of Appointment form and that documentation of this
                  authority is available upon request by Medicare.
                </span>
              </label>

              <div className="sales-form-fields" style={{ marginTop: 12 }}>
                <div className="sales-field sales-field-full">
                  <label>Beneficiary Submitted</label>
                  <input
                    value={
                      beneficiarySubmittedAt
                        ? new Date(beneficiarySubmittedAt).toLocaleString()
                        : "Not submitted"
                    }
                    disabled
                  />
                </div>
              </div>
            </div>

            <div className="section">
              <h3 className="section-title">Agent Information</h3>

              <div className="sales-form-fields">
                <div className="sales-field">
                  <label>Agent First Name</label>
                  <input value={agent.first_name || ""} disabled />
                </div>

                <div className="sales-field">
                  <label>Agent Last Name</label>
                  <input value={agent.last_name || ""} disabled />
                </div>
              </div>
            </div>

            <div className="section">
              <h3 className="section-title">Appointment Details</h3>

              <div className="sales-form-fields">
                <div className="sales-field">
                  <label>Appointment Date *</label>
                  <input
                    type="date"
                    value={appointmentDate}
                    onChange={(e) => setAppointmentDate(e.target.value)}
                  />
                </div>

                <div className="sales-field">
                  <label>Initial Method of Contact *</label>
                  <input
                    type="text"
                    value={initialMethodOfContact}
                    placeholder="Phone call, email, referral, walk-in, etc."
                    onChange={(e) => setInitialMethodOfContact(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="section">
              <h3 className="section-title">Agent Authorization</h3>

              <label className="soa-product-row">
                <input
                  type="checkbox"
                  checked={agreement}
                  onChange={() => setAgreement(!agreement)}
                />

                <span>
                  By checking this box, I confirm the information represented
                  here is true and accurate. I authorize my signature on the
                  Scope of Appointment form using this information.
                </span>
              </label>
            </div>

            <div className="soa-actions">
              <button
                className="sales-primary-btn"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting
                  ? "Completing SOA..."
                  : "Complete Scope of Appointment"}
              </button>

              {recordProfilePath && (
                <button
                  type="button"
                  className="sales-secondary-btn"
                  onClick={() => navigate(recordProfilePath)}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>

          <div className="sales-section-card">
            <div className="sales-section-head">
              <h2>Medicare Overview</h2>
              <p>Reference information included with the final SOA record.</p>
            </div>

            <div className="section">
              <p className="sales-secondary" style={{ lineHeight: 1.6 }}>
                Medicare Part C, or Medicare Advantage, combines the benefits of
                Medicare Parts A, B and typically D. Medicare Advantage plans
                typically come with additional benefits that Original Medicare
                doesn’t have and are distributed by private insurance companies.
                In many cases, you’ll need to use health care providers who
                participate in the plan’s network and service area for the lowest
                costs. These plans set a limit on what you’ll have to pay
                out-of-pocket each year for covered services, which could help
                protect you from unexpected costs.
              </p>
            </div>

            <div className="section">
              <h3 className="section-title">
                Stand-alone Medicare Prescription Drug Plan (Part D)
              </h3>

              <p className="sales-secondary" style={{ lineHeight: 1.6 }}>
                Medicare Part D only helps pay for prescription drugs — following
                federal rules. Part D is often added to Original Medicare (Part A
                and B) or Medicare Advantage plans that are offered by private
                insurance companies. You pay a monthly plan premium for Part D
                coverage to the insurance provider. Since the coverage rules of
                Medicare Part D are regulated by the federal government, the
                prescription drugs it covers may vary from year to year.
              </p>
            </div>

            <div className="section">
              <h3 className="section-title">Medicare Advantage Plans (Part C)</h3>

              <p className="sales-secondary" style={{ lineHeight: 1.6 }}>
                Medicare Part C, or Medicare Advantage, combines the benefits of
                Medicare Parts A, B and typically D. Medicare Advantage plans
                typically come with additional benefits that Original Medicare
                doesn’t have and are distributed by private insurance companies.
                In many cases, you’ll need to use health care providers who
                participate in the plan’s network and service area for the lowest
                costs. These plans set a limit on what you’ll have to pay
                out-of-pocket each year for covered services, which could help
                protect you from unexpected costs.
              </p>

              <p className="sales-secondary" style={{ lineHeight: 1.6 }}>
                Examples of Medicare Advantage Plans include:
              </p>

              <p className="sales-secondary" style={{ lineHeight: 1.6 }}>
                <strong>Medicare Health Maintenance Organization (HMO)</strong> –
                Provides all the benefits of Original Medicare and sometimes
                includes Part D. In most HMOs, you can only get your care from
                doctors or hospitals in the plan’s network (except in
                emergencies).
              </p>

              <p className="sales-secondary" style={{ lineHeight: 1.6 }}>
                <strong>Medicare Preferred Provider Organization (PPO) Plan</strong>{" "}
                – Provides all the benefits of Original Medicare and sometimes
                covers Part D. PPOs have network doctors and hospitals but you
                can also use out-of-network providers, typically at a higher cost.
              </p>

              <p className="sales-secondary" style={{ lineHeight: 1.6 }}>
                <strong>Medicare Private Fee-For-Services (PFFS) Plan</strong> –
                A plan that allows you to go to any Medicare-approved doctor,
                hospital and provider. The health provider must agree to treat
                you and accept the PFFS plan’s payment, terms and conditions. If
                your PFFS plan has a network, you can see any of the network
                providers who have agreed to treat members of the plan. You
                typically will pay more for out-of-network providers.
              </p>

              <p className="sales-secondary" style={{ lineHeight: 1.6 }}>
                <strong>Medicare Special Needs Plan (SNP)</strong> – A plan that
                has specific benefits intended for people with special health
                needs. Examples of special health needs include:
              </p>

              <ul className="sales-secondary" style={{ lineHeight: 1.6 }}>
                <li>People who qualify for both Medicare and Medicaid</li>
                <li>People in nursing homes</li>
                <li>People with approved chronic health conditions</li>
              </ul>
            </div>

            <div className="section">
              <h3 className="section-title">
                Medicare Supplement (Medigap) Products
              </h3>

              <p className="sales-secondary" style={{ lineHeight: 1.6 }}>
                Medigap is Medicare Supplement Insurance that helps fill “gaps”
                in Original Medicare and is sold by private companies. A Medicare
                Supplement Insurance (Medigap) policy can help pay some of the
                remaining health care costs, like:
              </p>

              <ul className="sales-secondary" style={{ lineHeight: 1.6 }}>
                <li>Copayments</li>
                <li>Coinsurance</li>
                <li>Deductibles</li>
              </ul>

              <p className="sales-secondary" style={{ lineHeight: 1.6 }}>
                You must have Medicare Part A and Part B to get a Medicare
                Supplement plan. A Medigap plan is different from Medicare Part C
                because it only supplements your Original Medicare benefits. So,
                Medigap does not include Original Medicare or Part D coverage by
                itself.
              </p>
            </div>

            <div className="section">
              <h3 className="section-title">Ancillary Products</h3>

              <p className="sales-secondary" style={{ lineHeight: 1.6 }}>
                You can get ancillary or secondary products through private
                insurance companies. These plans offer additional benefits to
                people who want separate coverage for things like dental, vision
                and hearing. Ancillary plans are not Medicare products.
              </p>
            </div>
          </div>
        </div>

        <Infobar
          record={record}
          relatedId={recordId}
          relatedType={recordType}
          onEmailClick={handleOpenEmailComposer}
        />
      </div>
    </>
  );
}

export default SOACompletePage;